import { formatDate } from "@/helpers/formatDate";
import { getIngredients } from "@/services/ingredients/api";
import { Ingredient } from "@/types/ingredient";
import { QueryParamsBase } from "@/types/types";
import { notification } from "antd";
import ExcelJS from "exceljs";
import { useState } from "react";

type UseExportInventoryProps = {
  tableParams?: QueryParamsBase;
};

export const useExportInventory = (props?: UseExportInventoryProps) => {
  const tableParams = props?.tableParams;
  const [api, contextHolder] = notification.useNotification();
  const [loadingButtonExport, setLoadingButtonExport] = useState<boolean>(false);

  const openNotification = (
    type: "success" | "error" | "info" | "warning",
    placement: "topLeft" | "topRight" | "bottomLeft",
    message: string,
    description: string,
  ) => {
    api[type]({
      message,
      description,
      duration: 2.5,
      placement,
    });
  };

  const generateExcel = async () => {
    try {
      setLoadingButtonExport(true);

      openNotification(
        "info",
        "topLeft",
        "La descarga del archivo Excel está en proceso.",
        "Te agradecemos tu paciencia.",
      );

      // Obtener todos los ingredientes aplicando filtros si existen
      const data = await getIngredients({
        ...tableParams,
        pageSize: 10000,
        current: 1,
      });

      const workBook = new ExcelJS.Workbook();
      const sheet = workBook.addWorksheet("Inventario");

      const fileName = "Inventario_Ingredientes.xlsx";

      // Columnas del Excel
      sheet.columns = [
        { header: "Nombre", key: "name", width: 30 },
        { header: "Tipo de Unidad", key: "unit", width: 15 },
        { header: "Stock Actual", key: "stock", width: 15 },
        { header: "Estado", key: "active", width: 15 },
      ];

      sheet.getRow(1).font = { bold: true };

      // Insertar filas
      (data?.items || []).forEach((item: Ingredient) => {
        sheet.addRow({
          name: item.name ?? "Sin nombre",
          unit: item.unit ?? "",
          stock: item.stock ?? 0,
          active: item.active ? "Activo" : "Inactivo",
        });
      });

      const buffer = await workBook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      window.URL.revokeObjectURL(url);

      openNotification(
        "success",
        "topLeft",
        "Documento descargado con éxito",
        "Ya puedes visualizar el archivo de inventario",
      );

      setLoadingButtonExport(false);
    } catch (error: any) {
      console.error("Error al exportar inventario:", error);

      openNotification(
        "error",
        "topLeft",
        "Error al exportar",
        "No se pudo generar el archivo Excel. Intenta nuevamente.",
      );

      setLoadingButtonExport(false);
    }
  };

  return {
    contextHolder,
    generateExcel,
    loadingButtonExport,
  };
};
